import { Providers } from "@amzn/ads-blink-client";
import { SortOptions } from "@amzn/ads-blink-client";

import { FeatureFlags } from "../../store/featureFlags";
import type { UnifiedFilterState } from "../../types/filters/common";
import type { AllUnifiedFilterKeys } from "../../types/filters/unified";
import {
    CoreUnifiedFilterKeys,
    UnifiedFilterField,
    UnifiedFilterKeys,
    UnifiedFilterReadableNames,
    UnifiedTypeGroups,
} from "../../types/filters/unified";
import type { FilterOptionsMap } from "../../utils/filters.utils";

export const unifiedFilters = [
    {
        label: "Brand",
        field: UnifiedFilterField.BRAND,
        isAdditional: true,
    },
    {
        label: "Browse node",
        field: UnifiedFilterField.BROWSE_NODE,
        isAdditional: true,
    },
    {
        label: "Objective",
        field: UnifiedFilterField.OBJECTIVE,
        isAdditional: true,
    },
    {
        label: "Topic",
        field: UnifiedFilterField.TOPIC,
        isAdditional: true,
    },
    {
        label: "Language",
        field: UnifiedFilterField.LANGUAGE,
        isAdditional: true,
    },
];

export const defaultUnifiedFilters = unifiedFilters.filter((filter) =>
    [UnifiedFilterField.BRAND, UnifiedFilterField.BROWSE_NODE].includes(
        filter.field as UnifiedFilterField,
    ),
);

export type UnifiedFiltersOptions = {
    [UnifiedFilterField.BRAND]: FilterOptionsMap;
    [UnifiedFilterField.BROWSE_NODE]: FilterOptionsMap;
    [UnifiedFilterField.OBJECTIVE]: FilterOptionsMap;
    [UnifiedFilterField.TOPIC]: FilterOptionsMap;
    [UnifiedFilterField.LANGUAGE]: FilterOptionsMap;
};

export const initialUnifiedFilterState: UnifiedFilterState = {
    searchQuery: "",
    isContentDirty: true,
    isMetadataDirty: true,
    sortAttribute: SortOptions.LAST_MODIFIED_DATE,
    initialVisibleFilters: [
        UnifiedFilterKeys.BRAND_NAME,
        UnifiedFilterKeys.BROWSE_NODE_NAME,
        UnifiedFilterKeys.LANGUAGE,
    ],
    lastSearchParameters: {
        selectedFilterIds: [],
        searchQuery: "",
    },
    lastSearchResultTotalCount: 0,
    filters: {
        [CoreUnifiedFilterKeys.TYPE]: [
            {
                id: "Ad Products UAT",
                tooltip:
                    "Packaged insights for Ad Products (e.g. AMC SIP, PVa, TNF, Path to Conversion, Advertising metrics Review, Campaign audience overlap)",
                label: "Ad products",
                isSelected: false,
                isDisabled: false,
                tag: {
                    label: "UAT",
                },
                enablingFeatureFlag: FeatureFlags.UAT_PACKAGED_INSIGHT,
                group: UnifiedTypeGroups.PACKAGED_UAT,
                scopes: [],
            },
            {
                id: "Advertiser Goals UAT",
                tooltip:
                    "Goal based packaged insights (e.g. BGF, New product launch)",
                label: "Advertiser goals",
                isSelected: false,
                isDisabled: false,
                tag: {
                    label: "UAT",
                },
                enablingFeatureFlag: FeatureFlags.UAT_PACKAGED_INSIGHT,
                group: UnifiedTypeGroups.PACKAGED_UAT,
                scopes: [],
            },
            {
                id: "Downstream Impact UAT",
                tooltip:
                    "New to Brand, ASIN level targeting, Repeat Purchase, Annualized Customer value, ASIN level sales performance",
                label: "Downstream impact",
                isSelected: false,
                isDisabled: false,
                tag: {
                    label: "UAT",
                },
                enablingFeatureFlag: FeatureFlags.UAT_PACKAGED_INSIGHT,
                group: UnifiedTypeGroups.PACKAGED_UAT,
                scopes: [],
            },
            {
                id: "Industry Insights UAT",
                tooltip:
                    "Industry specific trends (e.g. ACI, Industry quarterly trends, Insight case studies)",
                label: "Industry insights",
                isSelected: false,
                isDisabled: false,
                tag: {
                    label: "UAT",
                },
                enablingFeatureFlag: FeatureFlags.UAT_PACKAGED_INSIGHT,
                group: UnifiedTypeGroups.PACKAGED_UAT,
                scopes: [],
            },
            {
                id: "Seasonal Events UAT",
                tooltip:
                    "Packaged insights for seasonal events (e.g. Prime day, BFCM, Tentpole event recap)",
                label: "Seasonal events",
                isSelected: false,
                isDisabled: false,
                tag: {
                    label: "UAT",
                },
                enablingFeatureFlag: FeatureFlags.UAT_PACKAGED_INSIGHT,
                group: UnifiedTypeGroups.PACKAGED_UAT,
                scopes: [],
            },
            {
                id: "A&IPilotInsights",
                tooltip: "A&I insights modules created by AIPs",
                label: "A&I Insights",
                tag: {
                    label: "Pilot",
                },
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "AudienceFunnelSizing",
                tooltip:
                    "Audience funnel insights from APT (Quarterly Conversion Rates, Yearly Funnel Size)",
                label: "Audience funnel",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "BrandLoyalty",
                tooltip:
                    "AIP insights (NewToBrand, LongTimeValue, Order Distribution For Day of Week)",
                label: "Audience insights",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "BrandedSearchTrends",
                tooltip: "Weekly and Monthly Brand Search trends from NABS",
                label: "Branded search trends",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "PathToPurchase",
                tooltip: "Shopper journey from browsing to purchase from P2P",
                label: "Path to purchase",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "PeerBenchmarking",
                tooltip:
                    "Insights from Peer Scorecard (e.g. YTD Ad spend Peer Comparision, Investment Allocation by ad product Peer Comparision)",
                label: "Peer benchmarking",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "ShareOfVoice",
                tooltip:
                    "Insights from BSI (e.g. Branded keywords SOV, Peer set branded keywords, Ad Spend Correlation)",
                label: "Share of voice",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.STANDALONE,
                scopes: [Providers.INSIGHT],
            },
            {
                id: "CAIRE",
                tooltip:
                    "Advertiser specific insight based recommendations (OOB, keywords, Core 4)",
                label: "Caire recommendations",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.PACKAGE],
            },
            {
                id: "Ai Insights",
                tooltip: "A&I insights modules created by AIPs",
                label: "A&I Insights",
                tag: {
                    label: "Pilot",
                },
                isSelected: false,
                isDisabled: false,
                enablingFeatureFlag: FeatureFlags.AI_PACKAGED,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.TEMPLATES],
            },
            {
                id: "Ad Products",
                tooltip:
                    "Packaged insights for Ad Products (e.g. AMC SIP, PVa, TNF, Path to Conversion, Advertising metrics Review, Campaign audience overlap)",
                label: "Ad products",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.TEMPLATES],
            },
            {
                id: "Advertiser Goals",
                tooltip:
                    "Goal based packaged insights (e.g. BGF, New product launch)",
                label: "Advertiser goals",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.TEMPLATES],
            },
            {
                id: "Downstream Impact",
                tooltip:
                    "New to Brand, ASIN level targeting, Repeat Purchase, Annualized Customer value, ASIN level sales performance",
                label: "Downstream impact",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.TEMPLATES],
            },
            {
                id: "Industry Insights",
                tooltip:
                    "Industry specific trends (e.g. ACI, Industry quarterly trends, Insight case studies)",
                label: "Industry insights",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.TEMPLATES],
            },
            {
                id: "Seasonal Events",
                tooltip:
                    "Packaged insights for seasonal events (e.g. Prime day, BFCM, Tentpole event recap)",
                label: "Seasonal events",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.PACKAGED,
                scopes: [Providers.TEMPLATES],
            },
            {
                id: "Case Study",
                tooltip:
                    "A One-pager highlighting an advertiser success using Amazon Ad products",
                label: "Case study",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Guide",
                tooltip:
                    "Content used to advise the audience how to do something (ex. Creative Best Practices guide)",
                label: "Guide",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "LWP",
                tooltip: "Learning white paper",
                label: "LWP",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "One-sheeter",
                tooltip:
                    "A Single document that summarizes a product or concept",
                label: "One-sheeter",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Pitch Deck",
                tooltip:
                    "Sales presentation to persuade audience  to adopt an Amazon Ads product or service",
                label: "Pitch deck",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Primary Template",
                tooltip: "Amazon Ads powerpoint templates",
                label: "Primary template",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Product Card",
                tooltip:
                    "A Product handout that includes key information such as description, availability, audience, placement",
                label: "Product Card",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "The Brief",
                tooltip:
                    "A Series of supplemental slides to provide updates across the business, including recent news from Amazon's corporate blog, new insights, product updates, etc.",
                label: "The Brief",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Training",
                tooltip: "Content that teaches a skill",
                label: "Training",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Industry Insights - Marketing",
                tooltip: "Industry-specific insights and analysis",
                label: "Industry Insights",
                isSelected: true,
                isDisabled: false,
                group: UnifiedTypeGroups.MARKETING,
                scopes: [Providers.MARKETING_NARRATIVE],
            },
            {
                id: "Pitched Content",
                tooltip: "Client Presentations",
                label: "Client Presentations",
                isSelected: false,
                isDisabled: false,
                group: UnifiedTypeGroups.PITCHED_CONTENT,
                scopes: [Providers.PITCHED_CONTENT],
            },
        ],
        // The following keys will be populated by an API
        [UnifiedFilterKeys.PACKAGE_NAME]: [],
        [UnifiedFilterKeys.ENTITY_NAME]: [],
        [UnifiedFilterKeys.TOPIC]: [],
        [UnifiedFilterKeys.ADVERTISER_GOAL]: [],
        [UnifiedFilterKeys.AD_ACTIVATION_CHANNEL]: [],
        [UnifiedFilterKeys.SEASONAL_EVENTS]: [],
        [UnifiedFilterKeys.LANGUAGE]: [],
        [UnifiedFilterKeys.MARKETING_STRATEGY]: [],
        [UnifiedFilterKeys.SUB_VERTICAL]: [],
        [UnifiedFilterKeys.VERTICAL]: [],
        [UnifiedFilterKeys.FILE_TYPE]: [],
        [UnifiedFilterKeys.BRAND_NAME]: [],
        [UnifiedFilterKeys.MARKETPLACE]: [],
        [UnifiedFilterKeys.OBJECTIVES]: [],
        [UnifiedFilterKeys.PRIMARY_CONTENT_CREATOR_TEAM]: [],
        [UnifiedFilterKeys.BROWSE_NODE_NAME]: [],
        [UnifiedFilterKeys.AD_PRODUCTS_AND_FORMATS]: [],
        [UnifiedFilterKeys.MEETING_TYPE]: [],
        [UnifiedFilterKeys.MEETING_SUB_TYPE]: [],
        [UnifiedFilterKeys.OPPORTUNITY_CATEGORY]: [],
        [UnifiedFilterKeys.OPPORTUNITY_STATUS]: [],
    },
};

/**
 * Stores the string replacements we want to make on the search query to help users find more relevant content
 * e.g. "Share Of Voice" -> "ShareOfVoice"
 * TODO: Remove when synonym is released
 */
export const InsightSearchReplacements: Record<string, string> =
    initialUnifiedFilterState.filters.UNIFIED_TYPE.filter((x) =>
        x.scopes.includes(Providers.INSIGHT),
    ).reduce(
        (acc, item) => ({
            ...acc,
            [item.label.toLowerCase()]: item.id.toLowerCase(),
        }),
        {} as Record<string, string>,
    );

/**
 * Helper functions for grouping the different filter pills
 **/

// This is the type as defined by Storm, but it's not exported, so we have to delcare it ourselves
export type MultiSelectOption = {
    label: string;
    value?: string;
};

export type UnifiedFilterGroup = {
    groupName: string;
    groupValues: MultiSelectOption[];
};

export type UnifiedFilterGroupingFunctions = {
    [K in AllUnifiedFilterKeys]: (
        values: MultiSelectOption[],
    ) => UnifiedFilterGroup[];
};

const defaultGroupingFunction =
    <K extends AllUnifiedFilterKeys>(key: K) =>
    (values: MultiSelectOption[]) => [
        {
            groupName: UnifiedFilterReadableNames[key],
            groupValues: values,
        },
    ];

export const unifiedFilterGroupingFunctions: UnifiedFilterGroupingFunctions = {
    [CoreUnifiedFilterKeys.TYPE]: (values) => {
        // The groups are hard-coded into the filter, so we can look them up based on that value
        const groupedValues = values.reduce((acc, val) => {
            const group = initialUnifiedFilterState.filters[
                CoreUnifiedFilterKeys.TYPE
            ].find((type) => type.id === val.value)?.group;

            if (group) {
                if (!acc[group]) {
                    acc[group] = [];
                }
                acc[group].push(val);
            }
            return acc;
        }, {});

        return Object.entries(groupedValues).map(
            ([groupName, groupValues]) =>
                ({
                    groupName,
                    groupValues: groupValues as MultiSelectOption[],
                }) as UnifiedFilterGroup,
        );
    },
    [UnifiedFilterKeys.PACKAGE_NAME]: defaultGroupingFunction(
        UnifiedFilterKeys.PACKAGE_NAME,
    ),
    [UnifiedFilterKeys.ENTITY_NAME]: defaultGroupingFunction(
        UnifiedFilterKeys.ENTITY_NAME,
    ),
    [UnifiedFilterKeys.TOPIC]: defaultGroupingFunction(UnifiedFilterKeys.TOPIC),
    [UnifiedFilterKeys.ADVERTISER_GOAL]: defaultGroupingFunction(
        UnifiedFilterKeys.ADVERTISER_GOAL,
    ),
    [UnifiedFilterKeys.AD_ACTIVATION_CHANNEL]: defaultGroupingFunction(
        UnifiedFilterKeys.AD_ACTIVATION_CHANNEL,
    ),
    [UnifiedFilterKeys.SEASONAL_EVENTS]: defaultGroupingFunction(
        UnifiedFilterKeys.SEASONAL_EVENTS,
    ),
    [UnifiedFilterKeys.LANGUAGE]: defaultGroupingFunction(
        UnifiedFilterKeys.LANGUAGE,
    ),
    [UnifiedFilterKeys.MARKETING_STRATEGY]: defaultGroupingFunction(
        UnifiedFilterKeys.MARKETING_STRATEGY,
    ),
    [UnifiedFilterKeys.SUB_VERTICAL]: defaultGroupingFunction(
        UnifiedFilterKeys.SUB_VERTICAL,
    ),
    [UnifiedFilterKeys.VERTICAL]: defaultGroupingFunction(
        UnifiedFilterKeys.VERTICAL,
    ),
    [UnifiedFilterKeys.FILE_TYPE]: defaultGroupingFunction(
        UnifiedFilterKeys.FILE_TYPE,
    ),
    [UnifiedFilterKeys.BRAND_NAME]: (values) => {
        // Filter the brand names into two main groups, `BusinessLine` and `Brand` based on how their ids start
        const [businessLine, brand] = values.reduce(
            (acc, val) => {
                acc[val.value?.startsWith("ICM|BUSINESS_LINE") ? 0 : 1].push(
                    val,
                );
                return acc;
            },
            [[] as MultiSelectOption[], [] as MultiSelectOption[]],
        );

        return [
            { groupName: "Businessline", groupValues: businessLine },
            { groupName: "Brand", groupValues: brand },
        ];
    },
    [UnifiedFilterKeys.MARKETPLACE]: defaultGroupingFunction(
        UnifiedFilterKeys.MARKETPLACE,
    ),
    [UnifiedFilterKeys.OBJECTIVES]: defaultGroupingFunction(
        UnifiedFilterKeys.OBJECTIVES,
    ),
    [UnifiedFilterKeys.PRIMARY_CONTENT_CREATOR_TEAM]: defaultGroupingFunction(
        UnifiedFilterKeys.PRIMARY_CONTENT_CREATOR_TEAM,
    ),
    [UnifiedFilterKeys.BROWSE_NODE_NAME]: defaultGroupingFunction(
        UnifiedFilterKeys.BROWSE_NODE_NAME,
    ),
    [UnifiedFilterKeys.AD_PRODUCTS_AND_FORMATS]: defaultGroupingFunction(
        UnifiedFilterKeys.AD_PRODUCTS_AND_FORMATS,
    ),
    [UnifiedFilterKeys.MEETING_TYPE]: defaultGroupingFunction(
        UnifiedFilterKeys.MEETING_TYPE,
    ),
    [UnifiedFilterKeys.MEETING_SUB_TYPE]: defaultGroupingFunction(
        UnifiedFilterKeys.MEETING_SUB_TYPE,
    ),
    [UnifiedFilterKeys.OPPORTUNITY_CATEGORY]: defaultGroupingFunction(
        UnifiedFilterKeys.OPPORTUNITY_CATEGORY,
    ),
    [UnifiedFilterKeys.OPPORTUNITY_STATUS]: defaultGroupingFunction(
        UnifiedFilterKeys.OPPORTUNITY_STATUS,
    ),
};

export const SortDropdownItems = [
    {
        value: SortOptions.LAST_MODIFIED_DATE,
        label: "Last modified date",
    },
    {
        value: SortOptions.TYPE,
        label: "Type",
    },
];
